import { CircularProgress } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAllObjectSettings } from 'src/endpoints'

import { Modal } from '@/components/Modal/Modal'
import { useApi } from '@/endpoints/hooks'
import { MassExportConfigDto, ObjectSettingsDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { useGetUsedObjectSettingsUsingGetQuery } from '@/rtkApi/mddApiSliceGenerated'
import {
	MassExportExternalConfigDto,
	StructureTypeObjTypes,
	useGetObjectSettingsQuery,
	usePushReleaseToGitMutation,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { DdlModalType, MassExportSortingEnum } from '@/types'
import { NativeMap } from '@/utils/collections'

import { ExportConfigFields } from './ExportConfigFields'
import { ExportModalFooter } from './ExportModalFooter'
import { useAsyncMassExportV2 } from './hooks/useAsyncMassExportV2'
import {
	ObjectTypesFieldSet,
	StructureTypeButton,
	StyledCheckbox,
	StyledLegend,
} from './styles'
import { MassSqlExportModalProps, StructureStatusType } from './types'
import { getStructureIds } from './utils'

export const MassSqlExportModal = ({
	env,
	node,
	onClose,
	title,
	selectedNodes,
	onExport,
	open,
	enableExportStrategy = true,
	releaseId,
	type,
	saveTitle,
	isSqlGen,
}: MassSqlExportModalProps) => {
	const { t } = useAppContext()

	const objectSettings = useApi(getAllObjectSettings())

	const { data: sqlObjects, isLoading: isSqlObjectsLoading } =
		useGetUsedObjectSettingsUsingGetQuery({
			structureId: getStructureIds(selectedNodes, node?.id),
		})

	const { data: dpObjects, isLoading: isDpObjectsLoading } =
		useGetObjectSettingsQuery(
			{
				dpId: releaseId || 0,
			},
			{
				skip: !releaseId,
			},
		)

	const objectData = isSqlGen ? sqlObjects : dpObjects
	const isObjectTypesLoading =
		objectSettings.loading ||
		(isSqlGen ? isSqlObjectsLoading : isDpObjectsLoading)

	const [pushReleaseToGitMutation] = usePushReleaseToGitMutation()
	const { executeAsyncMassExport } = useAsyncMassExportV2()

	const [massExportStrategy, setMassExportStrategy] = useState(
		MassExportConfigDto.MassExportStrategyEnum.SINGLE_FILE,
	)

	const [massExportSorting, setMassExportSorting] = useState(
		MassExportSortingEnum.DEFAULT,
	)

	const [structureStatus, setStructureStatus] =
		useState<StructureStatusType[]>()

	const [selectedObjectSettings, setSelectedObjectSettings] = useState(
		[] as number[],
	)

	const [ignoreErrors, setIgnoreErrors] = useState(false)
	const [generateFromLastVersion, setGenerateFromLastVersion] = useState(true)
	const [generateFromLastTemplate, setGenerateFromLastTemplate] = useState(true)
	const [versionDate, setVersionDate] = useState<Date | null>(null)
	const [templateDate, setTemplateDate] = useState<Date | null>(null)

	const [exporting, setExporting] = useState(false)
	const [pushingGit, setPushingToGit] = useState(false)

	const getStructureTypeObjTypes = (): StructureTypeObjTypes => {
		const result: StructureTypeObjTypes = {}

		selectedObjectSettings.forEach((osId) => {
			const os = objectSettings?.data?.find((o) => o.id == osId)
			const structureType = os?.structureType as keyof StructureTypeObjTypes

			if (structureType) {
				if (!result[structureType]) {
					result[structureType] = []
				}
				result[structureType].push(os?.code)
			}
		})

		return result
	}

	const buildMassExportConfig = (): MassExportExternalConfigDto => ({
		massExportStrategy: massExportStrategy,
		massExportSorting: massExportSorting,
		structureObjectPairs: getStructureTypeObjTypes(),
		structureStatus:
			structureStatus as MassExportExternalConfigDto['structureStatus'],
		structureIds: getStructureIds(selectedNodes, node?.id),
		environmentCode: env?.code,
		ignoreErrors: ignoreErrors,
		structureTimestamp: generateFromLastVersion
			? undefined
			: versionDate?.toISOString(),
		templateTimestamp: generateFromLastTemplate
			? undefined
			: templateDate?.toISOString(),
	})

	const handleMassGitPush = async () => {
		setPushingToGit(true)

		const massExportExternalConfigDto = buildMassExportConfig()

		try {
			if (releaseId && env?.code) {
				await pushReleaseToGitMutation({
					dpId: releaseId,
					envCode: env.code,
					massExportExternalConfigDto,
				}).unwrap()

				toast(t('PUSH_TO_GIT_SUCCESS'), { type: 'success' })
				onClose()
			} else {
				await executeAsyncMassExport(massExportExternalConfigDto, setExporting)
			}
		} finally {
			setPushingToGit(false)
		}
	}

	const handleMassExport = async () => {
		const massExportExternalConfigDto = buildMassExportConfig()

		if (onExport) {
			onExport(massExportExternalConfigDto)

			return
		}

		setExporting(true)
		await executeAsyncMassExport(massExportExternalConfigDto, setExporting)
		onClose()
	}

	const massExportStrategies = useMemo(
		() => [
			{
				label: t('SINGLE_FILE'),
				value: MassExportConfigDto.MassExportStrategyEnum.SINGLE_FILE,
			},
			{
				label: t('ZIP_PER_STRUCTURE'),
				value: MassExportConfigDto.MassExportStrategyEnum.ZIP_PER_STRUCTURE,
			},
			{
				label: t('ZIP_OWNER_FOLDER_PER_OBJECT_TYPE'),
				value:
					MassExportConfigDto.MassExportStrategyEnum
						.ZIP_OWNER_FOLDER_PER_OBJECT_TYPE,
			},
			{
				label: t('ZIP_PER_OWNER_AND_OBJECT_TYPE'),
				value:
					MassExportConfigDto.MassExportStrategyEnum
						.ZIP_PER_OWNER_AND_OBJECT_TYPE,
			},
		],
		[t],
	)

	const massExportSortingOptions = useMemo(
		() => [
			{
				label: t('MASS_DOWNLOAD_SORTING_DEFAULT'),
				value: MassExportSortingEnum.DEFAULT,
			},
			{
				label: t('SORT_BY_NAME_ASC'),
				value: MassExportSortingEnum.SORT_BY_NAME_ASC,
			},
			{
				label: t('SORT_BY_TYPE_AND_NAME_ASC'),
				value: MassExportSortingEnum.SORT_BY_TYPE_AND_NAME_ASC,
			},
		],
		[t],
	)

	const objectSettingsMap = useMemo(() => {
		const map = (objectData || []).reduce(
			(pVal, cVal) => {
				const vKey = cVal.structureType?.toString() || ''

				if (pVal[vKey]) {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					pVal[vKey]!.push(cVal)
				} else {
					pVal[vKey] = [cVal]
				}

				return pVal
			},
			{} as NativeMap<ObjectSettingsDto[]>,
		)

		Object.values(map).forEach((items) => {
			items?.sort((a, b) =>
				(a.code ?? '').localeCompare(b.code ?? '', undefined, {
					sensitivity: 'base',
				}),
			)
		})

		return map
	}, [objectData])

	const sortedStructureTypeKeys = useMemo(
		() =>
			Object.keys(objectSettingsMap).sort((a, b) =>
				a.localeCompare(b, undefined, { sensitivity: 'base' }),
			),
		[objectSettingsMap],
	)

	useEffect(() => {
		if (
			type &&
			(type === DdlModalType.PushToGit || type === DdlModalType.CreateGit)
		) {
			const allOssIds = objectSettings?.data?.map((os) => os.id) || []
			setSelectedObjectSettings(allOssIds)
		}
	}, [objectSettings?.data, type])

	const handleGroupClick = (stKey: string) => {
		const ossIds =
			objectSettings?.data
				?.filter((os) => os.structureType === stKey)
				.map((os) => os.id) || []

		const isAllChecked = ossIds.every((ossId) =>
			selectedObjectSettings.some((oId) => oId === ossId),
		)

		if (isAllChecked) {
			setSelectedObjectSettings(
				selectedObjectSettings.filter((os) => !ossIds.some((o) => o === os)),
			)
		} else {
			setSelectedObjectSettings([...selectedObjectSettings, ...ossIds])
		}
	}

	const handleChecked = (isChecked: boolean, id: number) => {
		if (isChecked) {
			setSelectedObjectSettings([...selectedObjectSettings, id])
		} else {
			setSelectedObjectSettings(
				selectedObjectSettings.filter((osId) => osId !== id),
			)
		}
	}

	const isCheckBoxChecked = (os: ObjectSettingsDto): boolean => {
		return selectedObjectSettings.some((oId) => oId === os.id)
	}

	return (
		<Modal
			open={open ?? true}
			onClose={onClose}
			contentStyle={{ width: 600 }}
			header={title ? title : t('MASS_DOWNLOAD')}
			footer={
				<ExportModalFooter
					type={type}
					env={env}
					pushingGit={pushingGit}
					objectSettings={objectSettings}
					selectedObjectSettings={selectedObjectSettings}
					handleMassGitPush={handleMassGitPush}
					onClose={onClose}
					exporting={exporting}
					handleMassExport={handleMassExport}
					saveTitle={saveTitle}
					t={t}
				/>
			}
		>
			<ObjectTypesFieldSet>
				<StyledLegend>{t('OBJECT_SETTINGS')}</StyledLegend>

				{isObjectTypesLoading ? (
					<div className="w-full flex justify-center items-center py-8">
						<CircularProgress />
					</div>
				) : (
					sortedStructureTypeKeys.map((stKey) => (
						<div key={stKey}>
							<StructureTypeButton
								onClick={() => handleGroupClick(stKey)}
								schema="transparent"
							>
								{stKey}
							</StructureTypeButton>

							{objectSettingsMap[stKey]?.map((os) => {
								return (
									<StyledCheckbox
										name={`${stKey}--${os.code}`}
										key={os.id}
										title={os.code}
										value={isCheckBoxChecked(os)}
										onChange={(val) => handleChecked(val as boolean, os.id)}
									/>
								)
							})}
						</div>
					))
				)}
			</ObjectTypesFieldSet>

			<ExportConfigFields
				enableExportStrategy={enableExportStrategy}
				type={type}
				massExportStrategies={massExportStrategies}
				massExportSortingOptions={massExportSortingOptions}
				setMassExportStrategy={setMassExportStrategy}
				setMassExportSorting={setMassExportSorting}
				setIgnoreErrors={setIgnoreErrors}
				setGenerateFromLastVersion={setGenerateFromLastVersion}
				setGenerateFromLastTemplate={setGenerateFromLastTemplate}
				generateFromLastVersion={generateFromLastVersion}
				generateFromLastTemplate={generateFromLastTemplate}
				versionDate={versionDate}
				setVersionDate={setVersionDate}
				templateDate={templateDate}
				setTemplateDate={setTemplateDate}
				isSqlGen={isSqlGen}
				t={t}
				massExportStrategy={massExportStrategy}
				massExportSorting={massExportSorting}
				setStructureStatus={setStructureStatus}
			/>
		</Modal>
	)
}
